/**
 * 企业大脑 — 基础 API 封装
 * 支持 Token 自动续期和刷新
 * 支持多租户路径前缀
 */

// 从 URL 路径中提取租户标识
function getTenantFromPath(): string {
  const path = window.location.pathname;
  const parts = path.split('/').filter(Boolean);
  
  const nonTenantPaths = [
    'api', 'assets', 'health',
    'login', 'register', 'forgot-password', 'reset-password',
    'tools', 'apps', 'wins', 'knowledge', 'connectors', 'monitor-tasks',
    'webhooks', 'skillhub', 'call-management', 'sessions', 'docs',
    'deploy', 'remote', 'org', 'tenants', 'share', 's'
  ];
  if (parts.length > 0 && !nonTenantPaths.includes(parts[0])) {
    const tenantId = parts[0];
    if (/^[a-zA-Z0-9_-]{3,32}$/.test(tenantId)) {
      return tenantId;
    }
  }
  return '';
}

function getTenantPrefix(): string {
  const tenant = getTenantFromPath();
  return tenant ? `/${tenant}` : '';
}

export function getCurrentTenantId(): string {
  return getTenantFromPath() || 'default';
}

function getBaseUrl(): string {
  const tenantPrefix = getTenantPrefix();
  if (tenantPrefix) {
    return `${tenantPrefix}/api`;
  }
  return '/api';
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  raw?: boolean;
  skipRefresh?: boolean;
  _isRetry?: boolean;
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let pendingRequests: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

async function refreshToken(): Promise<string | null> {
  const token = localStorage.getItem('eb-token');
  if (!token) return null;

  // Guest 用户不支持 token 刷新
  const userStr = localStorage.getItem('eb-user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.role === 'guest') {
        return token; // guest 用户直接返回当前 token
      }
    } catch {
      // ignore
    }
  }

  try {
    const response = await fetch(`${getBaseUrl()}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      localStorage.removeItem('eb-token');
      return null;
    }

    const json = await response.json();
    const newToken = json.data?.token;
    if (newToken) {
      localStorage.setItem('eb-token', newToken);
      return newToken;
    }
    return null;
  } catch {
    return null;
  }
}

function shouldRefreshToken(response: Response): boolean {
  return response.headers.get('X-Token-Refresh') === 'recommended';
}

async function handleTokenRefresh(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  
  try {
    const newToken = await refreshToken();
    pendingRequests.forEach(({ resolve }) => resolve(newToken));
    pendingRequests = [];
    return newToken;
  } catch (error) {
    pendingRequests.forEach(({ reject }) => reject(error as Error));
    pendingRequests = [];
    throw error;
  } finally {
    isRefreshing = false;
  }
}

function triggerLogout() {
  localStorage.removeItem('eb-token');
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

export async function request<T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers, raw, skipRefresh, _isRetry, ...rest } = options;
  
  // 优先从 options 中获取 token，否则从 localStorage
  const token = (headers as Record<string, string>)?.Authorization?.replace('Bearer ', '') 
    || localStorage.getItem('eb-token');

  const config: RequestInit = {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(`${getBaseUrl()}${endpoint}`, config);

  if (!skipRefresh && shouldRefreshToken(response)) {
    handleTokenRefresh().catch(() => {});
  }

  if (!response.ok) {
    if (response.status === 401 && !_isRetry && !skipRefresh) {
      try {
        const newToken = await handleTokenRefresh();
        if (newToken) {
          return request<T>(endpoint, { ...options, _isRetry: true });
        }
      } catch {
        // 刷新失败
      }
      triggerLogout();
    }

    const errorData = await response.json().catch(() => ({}));
    const message =
      (errorData as { error?: string; message?: string }).error ||
      (errorData as { error?: string; message?: string }).message ||
      `请求失败 (${response.status})`;
    throw new Error(message);
  }

  const json = await response.json();
  if (!raw && json && typeof json === 'object' && 'data' in json) {
    return json.data as T;
  }
  return json as T;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    tenant_id?: string;
    avatar_color?: string;
    avatar_text?: string;
  };
}

export function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
}

export function guestLogin(tenantId?: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/guest', {
    method: 'POST',
    body: tenantId ? { tenant_id: tenantId } : {},
  });
}

export async function logout(): Promise<void> {
  const token = localStorage.getItem('eb-token');
  if (token) {
    try {
      await request('/auth/logout', {
        method: 'POST',
        skipRefresh: true,
      });
    } catch {
      // 忽略登出错误
    }
  }
  triggerLogout();
}

export async function logoutAll(): Promise<void> {
  try {
    await request('/auth/logout-all', {
      method: 'POST',
      skipRefresh: true,
    });
  } catch {
    // 忽略错误
  }
  triggerLogout();
}

export async function manualRefreshToken(): Promise<string | null> {
  return handleTokenRefresh();
}

export function isLoggedIn(): boolean {
  return !!localStorage.getItem('eb-token');
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  tenant_id?: string;
  avatar_color?: string;
  avatar_text?: string;
  avatar_url?: string;
}

export function updateProfile(data: UpdateProfileRequest): Promise<User> {
  return request<User>('/auth/profile', {
    method: 'PUT',
    body: data,
  });
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await request<void>('/auth/change-password', {
    method: 'POST',
    body: data,
  });
  triggerLogout();
}

export function getMe(): Promise<User> {
  return request<User>('/auth/me');
}
